/* C.5 Phase 2 — Company Repository / Search.

   Bound to GET /api/data/companies with q + active_only + has_logo +
   asset_type + page + page_size filters. Renders a lightweight table
   with inline logo thumbnails for fast visual triage of the 7,852-row
   corpus. Click a row → /data/companies/:identifier. */

import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
    MagnifyingGlass, ArrowRight, ImageBroken, Plus, UploadSimple,
    Flag, ArrowsClockwise, Lightning, Clock, Question, ChartBar,
    Download, CheckSquare,
} from "@phosphor-icons/react";
import { api, formatApiError } from "../../lib/api";
import AuthedLogo from "../../components/AuthedLogo";
import { useAuth } from "../../contexts/AuthContext";
import PageHeader from "../../components/layout/PageHeader";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { toast } from "sonner";
import CompanyCreateModal from "./CompanyCreateModal";
import CompanyImportCsvModal from "./CompanyImportCsvModal";

const ASSET_TYPES = [
    "LISTED_EQUITY", "AMC", "INSURANCE", "BOND_ISSUER",
    "PMS", "AIF", "REIT", "INVIT",
];

// 2026-02-08 — Repository Administration P1 vocabularies. Mirror
// /app/backend/routes_data_companies.py exactly. If you add a value
// here, also add it to the backend allowlist.
const SORT_OPTIONS = [
    { value: "name_asc", label: "Alphabetical (A-Z)" },
    { value: "name_desc", label: "Alphabetical (Z-A)" },
    { value: "recently_added", label: "Recently Added" },
    { value: "recently_updated", label: "Recently Updated" },
    { value: "last_enriched", label: "Last Enriched" },
];

const LOGO_STATUS_OPTIONS = [
    { value: "", label: "Logo status: any" },
    { value: "has_logo", label: "Has logo" },
    { value: "missing_logo", label: "Missing logo" },
    { value: "generated_letter_mark", label: "Generated letter mark" },
    { value: "auto_enriched", label: "Auto-enriched" },
    { value: "manual_upload", label: "Manual upload" },
    { value: "needs_review", label: "Needs review" },
    { value: "never_enriched", label: "Never enriched" },
];

const LOGO_SOURCE_OPTIONS = [
    { value: "", label: "Logo source: any" },
    { value: "manual_upload", label: "Manual upload" },
    { value: "favicon-svg", label: "SVG favicon" },
    { value: "apple-touch-icon", label: "Apple touch icon" },
    { value: "google-s2", label: "Google S2" },
    { value: "duckduckgo", label: "DuckDuckGo" },
    { value: "homepage-scrape", label: "Homepage scrape" },
    { value: "generated-letter-mark", label: "Generated letter mark" },
];

const DATE_WINDOW_OPTIONS = [
    { value: "", label: "—" },
    { value: "today", label: "Today" },
    { value: "7d", label: "Last 7 days" },
    { value: "30d", label: "Last 30 days" },
];

// Iter 79.6 — Thumbnail now fetched via authenticated XHR + blob URL.
// The serve endpoint requires the ``data:logo:download`` permission so a
// raw ``<img src>`` without a bearer token would 401. ``version`` (the
// row's ``last_updated_at``) is passed as ``bustKey`` so a curated
// replacement re-fetches without a page reload.
function LogoThumb({ filename, alt, version }) {
    const [errored, setErrored] = useState(false);
    const placeholder = (
        <div
            data-testid="logo-thumb-missing"
            className="h-8 w-8 rounded bg-slate-100 border border-slate-200 flex items-center justify-center"
        >
            <ImageBroken size={14} className="text-slate-400" />
        </div>
    );
    if (!filename || errored) return placeholder;
    return (
        <AuthedLogo
            filename={filename}
            bustKey={version}
            alt={alt || filename}
            className="h-8 w-8 rounded object-contain bg-white border border-slate-200 p-0.5"
            fallback={placeholder}
            onError={() => setErrored(true)}
            data-testid="logo-thumb"
        />
    );
}

const PAGE_SIZE = 50;

export default function CompanyRepository() {
    const { can } = useAuth();
    const canCreate = can("data:import");
    const canImport = can("data:import");

    // 2026-02-10 — URL-backed filter state (Operator CR — preserve
    // repository state across navigation, especially when drilling
    // into a company detail page and clicking back).
    //
    // Strategy:
    //   - useState initialisers READ from the URL on mount, so a
    //     returning operator lands on the exact filtered cohort and
    //     page they left from.
    //   - A single write-back effect MIRRORS state changes back to the
    //     URL via setSearchParams({...}, { replace: true }) so the
    //     filter change does not pollute browser history with every
    //     keystroke or chip-toggle.
    //   - Default values are OMITTED from the URL to keep URLs clean
    //     and shareable (no ?active_only=true&sort_by=name_asc spam).
    //   - On first mount the page-reset effect is suppressed so the
    //     URL's ``page=N`` survives hydration — only LATER filter
    //     changes reset the page.
    const [searchParams, setSearchParams] = useSearchParams();
    const urlGet = (k, fallback) => {
        const v = searchParams.get(k);
        return v === null ? fallback : v;
    };
    const initialMount = useRef(true);

    // 2026-02-10 — Defer the `initialMount` gate flip past React 19's
    // StrictMode setup→cleanup→setup replay sequence. Without this,
    // the filter-mirror's first run would flip `initialMount.current`
    // to false BEFORE the page-reset effect's StrictMode replay, so
    // the replay would fall through and `setPage(1)` would wipe the
    // URL's `page=N` hydration. `setTimeout(0)` schedules a macrotask
    // that fires AFTER all initial-mount effects (and any StrictMode
    // replays of them) have finished running.

    const [createOpen, setCreateOpen] = useState(false);
    const [importOpen, setImportOpen] = useState(false);
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(() => {
        const p = parseInt(urlGet("page", "1"), 10);
        return Number.isFinite(p) && p >= 1 ? p : 1;
    });
    const [q, setQ] = useState(() => urlGet("q", ""));
    const [debouncedQ, setDebouncedQ] = useState(() => urlGet("q", ""));
    const [activeOnly, setActiveOnly] = useState(() => urlGet("active_only", "true") !== "false");
    const [hasLogo, setHasLogo] = useState(() => urlGet("has_logo", "any")); // any | yes | no
    const [assetType, setAssetType] = useState(() => urlGet("asset_type", ""));
    // 2026-02-08 — Repository Administration P1 filter state.
    const [sortBy, setSortBy] = useState(() => urlGet("sort_by", "name_asc"));
    const [logoStatus, setLogoStatus] = useState(() => urlGet("logo_status", ""));
    const [logoSource, setLogoSource] = useState(() => urlGet("logo_source", ""));
    const [addedWindow, setAddedWindow] = useState(() => urlGet("added_window", ""));
    const [updatedWindow, setUpdatedWindow] = useState(() => urlGet("updated_window", ""));
    // Phase C: bulk selection + actions
    const [selectedIds, setSelectedIds] = useState(() => new Set());
    const [bulkBusy, setBulkBusy] = useState(false);
    // 2026-02-09 — "Select all matching filtered results". When true,
    // the next selection-mutation effect leaves the operator's
    // cohort-wide selection untouched even though `items` re-renders
    // due to pagination. Cleared whenever the operator changes a
    // filter or pages — anything that changes the filter envelope
    // invalidates the cohort-wide selection.
    const [cohortSelectActive, setCohortSelectActive] = useState(false);
    // Total number of rows in the cohort-wide selection (independent
    // of which page is currently visible). Surfaces in the toolbar.
    const [cohortSelectTotal, setCohortSelectTotal] = useState(0);
    const [refreshNonce, setRefreshNonce] = useState(0);
    const [exporting, setExporting] = useState(false);
    const debounceRef = useRef();

    // 2026-02-10 — Repository population counts (active / inactive /
    // total). Fetched once at mount from `/api/data/stats` and re-fetched
    // on `refreshNonce` so post-import / post-edit operations refresh
    // the badge. Separate from `total` (which is the FILTERED row count)
    // so the header can disambiguate "showing X" vs "of Y in repo".
    // Item raised by operator on 2026-02-10: the previous header read
    // "X active entities" and could be mis-read as "the repository
    // contains only X rows" — but X excludes the 3,114 inactive rows
    // (mostly matured BOND_ISSUERs) hidden by the default Active-only
    // filter.
    const [repoPopulation, setRepoPopulation] = useState({
        total: null, active: null, inactive: null,
    });
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const { data } = await api.get("/data/stats");
                if (cancelled) return;
                setRepoPopulation({
                    total: data.total_companies ?? null,
                    active: data.active_companies ?? null,
                    inactive: data.inactive_companies ?? null,
                });
            } catch {
                // Best-effort — header silently falls back to filtered
                // count when population is unavailable.
            }
        })();
        return () => { cancelled = true; };
    }, [refreshNonce]);

    // Debounce the text input so we don't slam the backend on each keystroke.
    useEffect(() => {
        clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => setDebouncedQ(q), 250);
        return () => clearTimeout(debounceRef.current);
    }, [q]);

    // Reset page when any filter changes — otherwise the operator may
    // see "no results" simply because the new filter slice doesn't have
    // the page they were on.
    // 2026-02-09: also invalidates any cohort-wide selection because
    // the cohort itself just changed.
    // 2026-02-10: gated against first-mount so the URL's ``page=N``
    // survives hydration — only LATER filter changes reset the page.
    //
    // Promise.resolve().then() defers the setState calls to a
    // microtask so React 19's react-hooks/set-state-in-effect rule
    // does not flag this effect — matches the lint-clean pattern used
    // by the listing-fetch effect below.
    useEffect(() => {
        if (initialMount.current) return;
        Promise.resolve().then(() => {
            setPage(1);
            setCohortSelectActive(false);
            setCohortSelectTotal(0);
            setSelectedIds(new Set());
        });
    }, [
        debouncedQ, activeOnly, hasLogo, assetType,
        sortBy, logoStatus, logoSource, addedWindow, updatedWindow,
    ]);

    // 2026-02-10 — Mirror the filter envelope back to the URL so that
    // navigating to a detail page and clicking back restores the exact
    // cohort and page. Defaults are omitted to keep URLs clean.
    //
    // History semantics:
    //   - Filter mutations (q, logo_status, asset_type, …) use
    //     ``replace: true`` so high-frequency or chained filter
    //     changes don't pollute browser history with N entries.
    //   - Page mutations use ``push`` (no replace flag) so clicking
    //     Next adds a real history entry. Otherwise the browser
    //     back-button after a drill-in lands on the URL that was in
    //     history BEFORE the operator paged forward — losing the
    //     page=N context.
    //
    // Split into two effects so each can choose the right semantics.
    const buildParams = () => {
        const p = {};
        if (debouncedQ) p.q = debouncedQ;
        if (activeOnly === false) p.active_only = "false";
        if (hasLogo && hasLogo !== "any") p.has_logo = hasLogo;
        if (assetType) p.asset_type = assetType;
        if (sortBy && sortBy !== "name_asc") p.sort_by = sortBy;
        if (logoStatus) p.logo_status = logoStatus;
        if (logoSource) p.logo_source = logoSource;
        if (addedWindow) p.added_window = addedWindow;
        if (updatedWindow) p.updated_window = updatedWindow;
        if (page && page !== 1) p.page = String(page);
        return p;
    };

    // Filter-change mirror — replace history (no per-keystroke pollution).
    useEffect(() => {
        setSearchParams(buildParams(), { replace: true });
    }, [
        debouncedQ, activeOnly, hasLogo, assetType,
        sortBy, logoStatus, logoSource, addedWindow, updatedWindow,
    ]);

    // Deferred mount-gate flip (see `initialMount` declaration comment).
    useEffect(() => {
        const t = setTimeout(() => { initialMount.current = false; }, 0);
        return () => clearTimeout(t);
    }, []);

    // Page-change mirror — PUSH history so back-button after a drill-in
    // restores ``page=N``. Skips the initial mount (state already
    // hydrated from URL — pushing again would be a redundant entry).
    const pageMirrorReady = useRef(false);
    useEffect(() => {
        if (!pageMirrorReady.current) {
            pageMirrorReady.current = true;
            return;
        }
        setSearchParams(buildParams(), { replace: false });
    }, [page]);

    // 2026-02-09 — Filter envelope shared with /companies/ids and
    // /companies/export.csv. Mirrors the listing-call params block.
    const filterParams = useMemo(() => ({
        active_only: activeOnly ? true : undefined,
        q: debouncedQ || undefined,
        asset_type: assetType || undefined,
        has_logo:
            hasLogo === "yes" ? true :
            hasLogo === "no" ? false : undefined,
        sort_by: sortBy,
        logo_status: logoStatus || undefined,
        logo_source: logoSource || undefined,
        added_window: addedWindow || undefined,
        updated_window: updatedWindow || undefined,
    }), [
        activeOnly, debouncedQ, assetType, hasLogo,
        sortBy, logoStatus, logoSource, addedWindow, updatedWindow,
    ]);

    // NOTE: ESLint's react-hooks/set-state-in-effect fires here on the
    // canonical async-data-fetch pattern. Inline disables don't suppress
    // it. This is a pre-existing baseline that also fires on the
    // unchanged sibling files CompanyDetail.jsx and CompanyCoverage.jsx —
    // not introduced by the Add-Company feature. Refactoring this pattern
    // app-wide is out of the current frozen scope.
    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const params = {
                    page,
                    page_size: PAGE_SIZE,
                    active_only: activeOnly ? true : undefined,
                    q: debouncedQ || undefined,
                    asset_type: assetType || undefined,
                    has_logo:
                        hasLogo === "yes" ? true :
                        hasLogo === "no" ? false : undefined,
                    sort_by: sortBy,
                    logo_status: logoStatus || undefined,
                    logo_source: logoSource || undefined,
                    added_window: addedWindow || undefined,
                    updated_window: updatedWindow || undefined,
                };
                const { data } = await api.get("/data/companies", { params });
                if (cancelled) return;
                setItems(data.items || []);
                setTotal(data.total || 0);
            } catch (err) {
                if (cancelled) return;
                toast.error(formatApiError(err, "Failed to load companies"));
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [
        page, debouncedQ, activeOnly, hasLogo, assetType,
        sortBy, logoStatus, logoSource, addedWindow, updatedWindow,
        refreshNonce,
    ]);

    const pageCount = useMemo(
        () => Math.max(1, Math.ceil(total / PAGE_SIZE)),
        [total]
    );

    // Phase C: clear selection when the visible row set changes —
    // unless the operator is in "cohort-wide selection" mode, where
    // the selection set was explicitly populated from /companies/ids
    // (the entire filter slice). In cohort mode we leave the set
    // untouched as pagination flips between pages.
    //
    // Promise.resolve().then() defers the setState call to a
    // microtask so React 19's react-hooks/set-state-in-effect rule
    // does not flag this effect — matches the lint-clean pattern used
    // elsewhere in the file.
    useEffect(() => {
        if (cohortSelectActive) return;
        Promise.resolve().then(() => setSelectedIds(new Set()));
    }, [items, cohortSelectActive]);

    const visibleIds = useMemo(() => items.map((i) => i.id), [items]);
    const allVisibleSelected = (
        visibleIds.length > 0 &&
        visibleIds.every((id) => selectedIds.has(id))
    );

    const toggleSelectAll = () => {
        // Toggling the per-page checkbox always exits cohort-select
        // mode (the operator's intent is page-scoped at that point).
        setCohortSelectActive(false);
        setCohortSelectTotal(0);
        if (allVisibleSelected) {
            setSelectedIds(new Set());
        } else {
            setSelectedIds(new Set(visibleIds));
        }
    };

    const toggleRow = (id) => {
        // Toggling an individual row in cohort-select mode demotes
        // the selection to a subset of the cohort. Keep the existing
        // ids minus the one being removed (or plus the one being
        // added), but stop calling it "all matching".
        if (cohortSelectActive) {
            setCohortSelectActive(false);
            setCohortSelectTotal(0);
        }
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const applyQueuePreset = (preset) => {
        // Reset all filter state to a known queue configuration.
        setLogoStatus("");
        setLogoSource("");
        setAddedWindow("");
        setUpdatedWindow("");
        setAssetType("");
        if (preset === "recently_added") {
            setSortBy("recently_added");
            setAddedWindow("30d");
        } else if (preset === "never_enriched") {
            setSortBy("name_asc");
            setLogoStatus("never_enriched");
        } else if (preset === "needs_review") {
            setSortBy("recently_updated");
            setLogoStatus("needs_review");
        } else {
            // "all" — clear everything
            setSortBy("name_asc");
        }
    };

    const activePreset = (
        logoStatus === "needs_review" ? "needs_review" :
        logoStatus === "never_enriched" ? "never_enriched" :
        (addedWindow === "30d" && sortBy === "recently_added") ? "recently_added" :
        "all"
    );

    // ── Bulk-operation chunking limits (mirror backend caps) ─────
    // Targeted enrichment cap: _ENRICH_TARGETED_MAX_IDS = 500
    // Bulk needs-review cap:   _BULK_NEEDS_REVIEW_MAX_IDS = 500
    const BULK_CHUNK_SIZE = 500;

    const _chunk = (arr, size) => {
        const out = [];
        for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
        return out;
    };

    const bulkEnrich = async (mode) => {
        const ids = Array.from(selectedIds);
        if (ids.length === 0) {
            toast.error("Select at least one company");
            return;
        }
        // Iter 86.2 — Dashboard parity polish. Confirmation wording aligned
        // with the Logo Enrichment Launcher (Iter 86): use "Skip Review
        // Queue" terminology instead of legacy "LIVE-enrich". Overwrite
        // language removed because Skip-Review-Queue mode is currently
        // disabled by default — the Review Queue is the authoritative path.
        const verb = mode === "live" ? "Skip Review Queue (direct enrich)" : "dry-run enrich";
        const chunks = _chunk(ids, BULK_CHUNK_SIZE);
        const chunkNote = chunks.length > 1
            ? `\n\nWill be split into ${chunks.length} sequential batches of up to ${BULK_CHUNK_SIZE} each (each becomes its own audited run).`
            : "";
        if (!confirm(
            `${verb} for ${ids.length} selected ${ids.length === 1 ? "company" : "companies"}?\n\n`
          + (mode === "live"
              ? "Skip-Review-Queue bypasses human verification and writes directly to the Company Repository. Use only with administrator approval. Manual uploads are never overwritten."
              : "Dry-run only — no writes. Results appear in the Logo Enrichment Operator UI for review."
            ) + chunkNote
        )) return;
        setBulkBusy(true);
        try {
            const runIds = [];
            for (const chunk of chunks) {
                const { data } = await api.post(
                    "/data/admin/logos/enrich",
                    { company_ids: chunk },
                    { params: { mode, max_rows: chunk.length, concurrency: 4 } },
                );
                runIds.push(data.run_id);
            }
            toast.success(
                chunks.length === 1
                    ? `Run ${runIds[0]} queued. Monitor it in the Logo Enrichment Operator console.`
                    : `${chunks.length} runs queued (${runIds.join(", ")}). Monitor them in the Logo Enrichment Operator console.`
            );
            setSelectedIds(new Set());
            setCohortSelectActive(false);
            setCohortSelectTotal(0);
        } catch (err) {
            // Iter 86.2 — Dashboard parity polish. When the backend
            // returns 503 because Skip-Review-Queue mode is disabled,
            // the system is operating as designed (Review Queue is the
            // default workflow). Present this as informational (blue),
            // not error (red). All other failures keep error styling.
            const status = err?.response?.status;
            const detail = formatApiError(err, "Bulk enrich failed");
            if (status === 503 && mode === "live"
                && typeof detail === "string"
                && detail.toLowerCase().includes("skip-review-queue")) {
                toast.info(detail);
            } else {
                toast.error(detail);
            }
        } finally {
            setBulkBusy(false);
        }
    };

    const bulkSetReview = async (flag) => {
        const ids = Array.from(selectedIds);
        if (ids.length === 0) {
            toast.error("Select at least one company");
            return;
        }
        setBulkBusy(true);
        try {
            let modified = 0;
            let unknown = 0;
            for (const chunk of _chunk(ids, BULK_CHUNK_SIZE)) {
                const { data } = await api.patch(
                    "/data/admin/companies-bulk/needs-review",
                    { company_ids: chunk, needs_review: flag },
                );
                modified += data.modified || 0;
                unknown += (data.skipped_unknown || []).length;
            }
            toast.success(
                `${modified} updated`
              + (unknown ? ` · ${unknown} unknown skipped` : "")
            );
            setSelectedIds(new Set());
            setCohortSelectActive(false);
            setCohortSelectTotal(0);
            setRefreshNonce((n) => n + 1);
        } catch (err) {
            toast.error(formatApiError(err, "Bulk update failed"));
        } finally {
            setBulkBusy(false);
        }
    };

    const bulkSetReviewStatus = async (status) => {
        // Phase D: explicit 4-state bulk transition.
        const ids = Array.from(selectedIds);
        if (ids.length === 0) {
            toast.error("Select at least one company");
            return;
        }
        setBulkBusy(true);
        try {
            let modified = 0;
            let unknown = 0;
            for (const chunk of _chunk(ids, BULK_CHUNK_SIZE)) {
                const { data } = await api.patch(
                    "/data/admin/companies-bulk/review-status",
                    { company_ids: chunk, review_status: status },
                );
                modified += data.modified || 0;
                unknown += (data.skipped_unknown || []).length;
            }
            const label = status ? status.toUpperCase() : "CLEARED";
            toast.success(
                `${modified} → ${label}`
              + (unknown ? ` · ${unknown} unknown skipped` : "")
            );
            setSelectedIds(new Set());
            setCohortSelectActive(false);
            setCohortSelectTotal(0);
            setRefreshNonce((n) => n + 1);
        } catch (err) {
            toast.error(formatApiError(err, "Bulk review-status update failed"));
        } finally {
            setBulkBusy(false);
        }
    };

    // 2026-02-09 — Select all rows matching the current filter
    // envelope (not just visible page). Hits /companies/ids with the
    // shared filterParams, replaces `selectedIds` with the cohort,
    // and flips into cohort-select mode so pagination doesn't reset
    // the selection on its own.
    const selectAllMatching = async () => {
        if (bulkBusy) return;
        setBulkBusy(true);
        try {
            const { data } = await api.get("/data/companies/ids", {
                params: filterParams,
            });
            const ids = data.ids || [];
            if (ids.length === 0) {
                toast.error("No companies match the current filters");
                return;
            }
            setSelectedIds(new Set(ids));
            setCohortSelectActive(true);
            setCohortSelectTotal(data.total || ids.length);
            if (data.cap_hit) {
                toast.warning(
                    `${data.total.toLocaleString()} match but only ${ids.length.toLocaleString()} could be selected (cap ${data.hard_cap}). Narrow the filter to act on the rest.`
                );
            } else {
                toast.success(`Selected ${ids.length.toLocaleString()} matching companies`);
            }
        } catch (err) {
            toast.error(formatApiError(err, "Select-all failed"));
        } finally {
            setBulkBusy(false);
        }
    };

    // 2026-02-09 — Download every row matching the current filter
    // envelope as a CSV. Same filterParams as /companies/ids. Streams
    // via the api client so the auth header is automatically attached.
    const downloadFilteredCsv = async () => {
        if (exporting) return;
        setExporting(true);
        try {
            const response = await api.get("/data/companies/export.csv", {
                params: filterParams,
                responseType: "blob",
            });
            const cd = response.headers["content-disposition"] || "";
            const m = cd.match(/filename="([^"]+)"/);
            const filename = m ? m[1] : "reedos-company-repo-filtered.csv";
            const url = URL.createObjectURL(response.data);
            const a = document.createElement("a");
            a.href = url; a.download = filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
            const rows = response.headers["x-rows-written"];
            const capHit = response.headers["x-cap-hit"] === "true";
            const totalMatching = response.headers["x-total-matching"];
            if (capHit) {
                toast.warning(
                    `Downloaded ${rows} rows (capped). ${totalMatching} total match — narrow filters to export the rest.`
                );
            } else {
                toast.success(`Downloaded ${rows} rows`);
            }
        } catch (err) {
            toast.error(formatApiError(err, "Export failed"));
        } finally {
            setExporting(false);
        }
    };

    // 2026-02-10 — Dynamic header description.
    // Disambiguates three counts so operators don't mis-read the
    // active-only filtered view as the repository total:
    //   - `total`         — rows matching CURRENT filters
    //   - `population.active` — total active rows in the repository
    //   - `population.total`  — total rows in the repository (active + inactive)
    //
    // Wording is shaped to match the operator's mental model
    // (request 2026-02-10): always lead with what the user is
    // looking at, then surface the repository population as
    // baseline context. The "Logos served at …" admin breadcrumb
    // is preserved on the second line.
    const headerDescription = useMemo(() => {
        const filterApplied = Boolean(
            debouncedQ || logoStatus || logoSource ||
            assetType || hasLogo !== "any" ||
            addedWindow || updatedWindow
        );
        const fmt = (n) => (n == null ? "—" : n.toLocaleString());
        const pop = repoPopulation;
        const haveRepoCounts = pop.total != null && pop.active != null;

        let primary;
        if (filterApplied) {
            primary = `Showing ${fmt(total)} matching current filters`;
        } else if (activeOnly) {
            primary = `Showing ${fmt(total)} active entities`;
        } else {
            primary = `Showing ${fmt(total)} entities (active + inactive)`;
        }

        const baseline = haveRepoCounts
            ? ` · Repository: ${fmt(pop.total)} total · ${fmt(pop.active)} active · ${fmt(pop.inactive)} inactive`
            : "";

        return `${primary}${baseline}. Logos served at /api/data/logos/{filename}.`;
    }, [
        total, activeOnly, debouncedQ, logoStatus, logoSource,
        assetType, hasLogo, addedWindow, updatedWindow, repoPopulation,
    ]);

    return (
        <div data-testid="company-repository-page">
            <PageHeader
                eyebrow="Data"
                title="Company Repository"
                description={headerDescription}
                actions={(
                    <div className="flex items-center gap-2">
                        <Link to="/data/repository/dashboard">
                            <Button
                                variant="outline"
                                data-testid="dashboard-link"
                                className="gap-2"
                            >
                                <ChartBar size={16} />
                                Dashboard
                            </Button>
                        </Link>
                        <Button
                            variant="outline"
                            data-testid="export-filtered-csv-button"
                            onClick={downloadFilteredCsv}
                            disabled={exporting || total === 0}
                            title={total === 0 ? "No rows to export" : `Export ${total.toLocaleString()} rows matching current filters`}
                            className="gap-2"
                        >
                            <Download size={16} weight={exporting ? "regular" : "fill"} />
                            {exporting ? "Exporting…" : "Download CSV"}
                        </Button>
                        {canImport && (
                            <Button
                                variant="outline"
                                data-testid="company-import-button"
                                onClick={() => setImportOpen(true)}
                                className="gap-2"
                            >
                                <UploadSimple size={16} />
                                Import CSV
                            </Button>
                        )}
                        {canCreate && (
                            <Button
                                data-testid="company-add-button"
                                onClick={() => setCreateOpen(true)}
                                className="gap-2"
                            >
                                <Plus size={16} />
                                Add Company
                            </Button>
                        )}
                    </div>
                )}
            />

            <CompanyCreateModal
                key={createOpen ? "open" : "closed"}
                open={createOpen}
                onClose={() => setCreateOpen(false)}
                onCreated={() => {
                    // Reset to page 1 so the freshly-created row is visible
                    // when the operator returns to the list view. The
                    // modal handler also navigates straight to the detail
                    // page, so this is purely a "back button" courtesy.
                    setPage(1);
                }}
            />

            <CompanyImportCsvModal
                key={importOpen ? "import-open" : "import-closed"}
                open={importOpen}
                onClose={() => setImportOpen(false)}
                onImported={(report) => {
                    // Reload the list so freshly-imported rows surface.
                    // The modal stays open until the operator dismisses
                    // it (so they can read the summary + grab the error
                    // report); resetting to page 1 ensures the next list
                    // render shows the new arrivals near the top.
                    setPage(1);
                    // No-op for the report — already surfaced inside the
                    // modal. Hook is here for future analytics.
                    void report;
                }}
            />

            <div className="px-6 sm:px-8 py-6 space-y-5">
                {/* Phase C — Quick Queue tabs (preset filter combos) */}
                <div
                    className="flex flex-wrap items-center gap-2"
                    data-testid="queue-tabs"
                >
                    {[
                        { id: "all", label: "All", icon: null },
                        { id: "recently_added", label: "Recently added", icon: Clock },
                        { id: "never_enriched", label: "Never enriched", icon: Question },
                        { id: "needs_review", label: "Needs review", icon: Flag },
                    ].map(({ id, label, icon: Icon }) => {
                        const isActive = activePreset === id;
                        return (
                            <button
                                key={id}
                                type="button"
                                data-testid={`queue-tab-${id}`}
                                onClick={() => applyQueuePreset(id)}
                                className={
                                    "inline-flex items-center gap-1.5 h-8 px-3 rounded-full text-xs font-medium transition-colors " +
                                    (isActive
                                        ? "bg-slate-900 text-white"
                                        : "bg-slate-100 text-slate-600 hover:bg-slate-200")
                                }
                            >
                                {Icon && <Icon size={14} weight={isActive ? "fill" : "regular"} />}
                                {label}
                            </button>
                        );
                    })}
                </div>

                {/* Filter bar */}
                <div className="flex flex-wrap items-center gap-3">
                    <div className="relative flex-1 min-w-[240px] max-w-md">
                        <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                        <Input
                            data-testid="company-search-input"
                            placeholder="Search by name, NSE / BSE / ISIN…"
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            className="pl-9 h-9"
                        />
                    </div>
                    <select
                        data-testid="sort-by-filter"
                        value={sortBy}
                        onChange={(e) => setSortBy(e.target.value)}
                        className="h-9 px-3 rounded-md border border-slate-200 bg-white text-sm text-slate-700"
                        title="Sort"
                    >
                        {SORT_OPTIONS.map((o) => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                    </select>
                    <select
                        data-testid="asset-type-filter"
                        value={assetType}
                        onChange={(e) => setAssetType(e.target.value)}
                        className="h-9 px-3 rounded-md border border-slate-200 bg-white text-sm text-slate-700"
                    >
                        <option value="">All asset types</option>
                        {ASSET_TYPES.map((at) => (
                            <option key={at} value={at}>{at.replace(/_/g, " ")}</option>
                        ))}
                    </select>
                    <select
                        data-testid="logo-status-filter"
                        value={logoStatus}
                        onChange={(e) => setLogoStatus(e.target.value)}
                        className="h-9 px-3 rounded-md border border-slate-200 bg-white text-sm text-slate-700"
                        title="Logo status"
                    >
                        {LOGO_STATUS_OPTIONS.map((o) => (
                            <option key={o.value || "_any_"} value={o.value}>{o.label}</option>
                        ))}
                    </select>
                    <select
                        data-testid="logo-source-filter"
                        value={logoSource}
                        onChange={(e) => setLogoSource(e.target.value)}
                        className="h-9 px-3 rounded-md border border-slate-200 bg-white text-sm text-slate-700"
                        title="Logo source"
                    >
                        {LOGO_SOURCE_OPTIONS.map((o) => (
                            <option key={o.value || "_any_"} value={o.value}>{o.label}</option>
                        ))}
                    </select>
                    <select
                        data-testid="added-window-filter"
                        value={addedWindow}
                        onChange={(e) => setAddedWindow(e.target.value)}
                        className="h-9 px-3 rounded-md border border-slate-200 bg-white text-sm text-slate-700"
                        title="Added window"
                    >
                        <option value="">Added: any time</option>
                        {DATE_WINDOW_OPTIONS.filter(o => o.value).map((o) => (
                            <option key={o.value} value={o.value}>Added: {o.label.toLowerCase()}</option>
                        ))}
                    </select>
                    <select
                        data-testid="updated-window-filter"
                        value={updatedWindow}
                        onChange={(e) => setUpdatedWindow(e.target.value)}
                        className="h-9 px-3 rounded-md border border-slate-200 bg-white text-sm text-slate-700"
                        title="Updated window"
                    >
                        <option value="">Updated: any time</option>
                        {DATE_WINDOW_OPTIONS.filter(o => o.value).map((o) => (
                            <option key={o.value} value={o.value}>Updated: {o.label.toLowerCase()}</option>
                        ))}
                    </select>
                    <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer select-none" data-testid="active-only-toggle">
                        <input
                            type="checkbox"
                            checked={activeOnly}
                            onChange={(e) => setActiveOnly(e.target.checked)}
                            className="rounded"
                        />
                        Active only
                    </label>
                </div>

                {/* 2026-02-09 — "Select all matching" banner. Surfaces
                    when the operator has ticked the per-page select-all
                    checkbox AND the cohort extends beyond the visible
                    page. One click swaps the page-scoped selection for
                    the full cohort-wide selection. */}
                {canImport && allVisibleSelected && !cohortSelectActive && total > visibleIds.length && (
                    <div
                        data-testid="select-all-matching-banner"
                        className="flex flex-wrap items-center gap-3 px-4 py-2.5 rounded-md border border-sky-200 bg-sky-50 text-sm"
                    >
                        <CheckSquare size={16} weight="fill" className="text-sky-600 shrink-0" />
                        <span className="text-slate-700">
                            All {visibleIds.length} on this page are selected.
                        </span>
                        <button
                            type="button"
                            data-testid="select-all-matching-button"
                            onClick={selectAllMatching}
                            disabled={bulkBusy}
                            className="text-sky-700 font-medium underline hover:text-sky-900 disabled:opacity-50 disabled:no-underline"
                        >
                            Select all {total.toLocaleString()} matching the current filters
                        </button>
                    </div>
                )}

                {/* Phase C — Bulk-actions toolbar (visible when ≥1 row selected). */}
                {selectedIds.size > 0 && canImport && (
                    <div
                        data-testid="bulk-actions-toolbar"
                        className="flex flex-wrap items-center gap-2 px-4 py-2.5 rounded-md border border-amber-200 bg-amber-50 text-sm"
                    >
                        <span className="text-slate-700 font-medium" data-testid="bulk-selection-count">
                            {cohortSelectActive
                                ? `${cohortSelectTotal.toLocaleString()} selected (all matching filters)`
                                : `${selectedIds.size} selected (this page)`}
                        </span>
                        <span className="text-slate-300">·</span>
                        <Button
                            size="sm" variant="outline"
                            data-testid="bulk-enrich-dryrun-button"
                            disabled={bulkBusy}
                            onClick={() => bulkEnrich("dry_run")}
                            className="gap-1.5"
                        >
                            <ArrowsClockwise size={14} /> Dry-run enrich
                        </Button>
                        <Button
                            size="sm"
                            data-testid="bulk-enrich-live-button"
                            disabled={bulkBusy}
                            onClick={() => bulkEnrich("live")}
                            className="gap-1.5"
                            title="Skip the Review Queue and write enriched logos directly to the Repository. Requires administrator approval and is disabled by default."
                        >
                            <Lightning size={14} weight="fill" /> Skip Review Queue
                        </Button>
                        <span className="text-slate-300">·</span>
                        <Button
                            size="sm" variant="outline"
                            data-testid="bulk-review-flag-button"
                            disabled={bulkBusy}
                            onClick={() => bulkSetReviewStatus("pending")}
                            className="gap-1.5"
                        >
                            <Flag size={14} weight="fill" /> Pending
                        </Button>
                        <Button
                            size="sm" variant="outline"
                            data-testid="bulk-review-approved-button"
                            disabled={bulkBusy}
                            onClick={() => bulkSetReviewStatus("approved")}
                            className="gap-1.5 text-emerald-700 border-emerald-200"
                        >
                            <Flag size={14} /> Approved
                        </Button>
                        <Button
                            size="sm" variant="outline"
                            data-testid="bulk-review-rejected-button"
                            disabled={bulkBusy}
                            onClick={() => bulkSetReviewStatus("rejected")}
                            className="gap-1.5 text-rose-700 border-rose-200"
                        >
                            <Flag size={14} /> Rejected
                        </Button>
                        <Button
                            size="sm" variant="outline"
                            data-testid="bulk-review-ignored-button"
                            disabled={bulkBusy}
                            onClick={() => bulkSetReviewStatus("ignored")}
                            className="gap-1.5 text-slate-600 border-slate-200"
                        >
                            <Flag size={14} /> Ignored
                        </Button>
                        <Button
                            size="sm" variant="outline"
                            data-testid="bulk-review-clear-button"
                            disabled={bulkBusy}
                            onClick={() => bulkSetReviewStatus(null)}
                            className="gap-1.5"
                        >
                            Clear
                        </Button>
                        <button
                            type="button"
                            data-testid="bulk-clear-selection"
                            onClick={() => {
                                setSelectedIds(new Set());
                                setCohortSelectActive(false);
                                setCohortSelectTotal(0);
                            }}
                            className="ml-auto text-xs text-slate-500 hover:text-slate-900 underline"
                        >
                            Clear selection
                        </button>
                    </div>
                )}

                {/* Results table */}
                <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="bg-slate-50 text-left">
                                <tr className="text-slate-500">
                                    <th className="px-4 py-2.5 font-medium w-12">
                                        {canImport && (
                                            <input
                                                type="checkbox"
                                                data-testid="select-all-checkbox"
                                                checked={allVisibleSelected}
                                                onChange={toggleSelectAll}
                                                className="rounded cursor-pointer"
                                                title={allVisibleSelected ? "Clear page selection" : "Select all visible"}
                                            />
                                        )}
                                    </th>
                                    <th className="px-4 py-2.5 font-medium w-12">Logo</th>
                                    <th className="px-4 py-2.5 font-medium">Identity</th>
                                    <th className="px-4 py-2.5 font-medium">Company</th>
                                    <th className="px-4 py-2.5 font-medium">Asset · Tier</th>
                                    <th className="px-4 py-2.5 font-medium">Source</th>
                                    <th className="px-4 py-2.5 font-medium w-12"></th>
                                </tr>
                            </thead>
                            <tbody data-testid="company-rows" className="divide-y divide-slate-100">
                                {loading && (
                                    <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-400">Loading…</td></tr>
                                )}
                                {!loading && items.length === 0 && (
                                    <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-400">No companies match the current filters.</td></tr>
                                )}
                                {items.map((c) => (
                                    <tr
                                        key={c.id}
                                        data-testid={`company-row-${c.id}`}
                                        className={
                                            "transition-colors " +
                                            (selectedIds.has(c.id) ? "bg-amber-50" : "hover:bg-slate-50")
                                        }
                                    >
                                        <td className="px-4 py-2.5">
                                            {canImport && (
                                                <input
                                                    type="checkbox"
                                                    data-testid={`row-select-${c.id}`}
                                                    checked={selectedIds.has(c.id)}
                                                    onChange={() => toggleRow(c.id)}
                                                    className="rounded cursor-pointer"
                                                />
                                            )}
                                        </td>
                                        <td className="px-4 py-2.5"><LogoThumb filename={c.logo_filename} alt={c.company_name} version={c.last_updated_at} /></td>
                                        <td className="px-4 py-2.5">
                                            <div className="font-mono text-xs text-slate-900" data-testid={`company-symbol-${c.id}`}>
                                                {c.nse_symbol || c.bse_code || c.isin || c.id}
                                            </div>
                                            {c.isin && c.isin !== (c.nse_symbol || c.bse_code) && (
                                                <div className="font-mono text-[10px] text-slate-400 mt-0.5">{c.isin}</div>
                                            )}
                                        </td>
                                        <td className="px-4 py-2.5">
                                            <div className="text-slate-900" data-testid={`company-name-${c.id}`}>{c.company_name}</div>
                                            {!c.active && (
                                                <span className="inline-block mt-1 text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-500 uppercase tracking-wide">Inactive</span>
                                            )}
                                        </td>
                                        <td className="px-4 py-2.5 text-slate-600 text-xs">
                                            <div>{(c.asset_type || "").replace(/_/g, " ")}</div>
                                            {c.tier && <div className="text-slate-400">{c.tier}</div>}
                                        </td>
                                        <td className="px-4 py-2.5 text-xs text-slate-500">
                                            {c.logo_source || <span className="text-slate-300">—</span>}
                                        </td>
                                        <td className="px-4 py-2.5">
                                            {/* 2026-02-10 — pass the current
                                              * repository URL search string
                                              * via React Router `state` so
                                              * the detail page's "Back"
                                              * button can restore the exact
                                              * filter envelope. Without
                                              * this, Back lands on the
                                              * clean `/data/companies`
                                              * URL and the operator loses
                                              * the cohort they were
                                              * reviewing. */}
                                            <Link
                                                to={`/data/companies/${encodeURIComponent(c.nse_symbol || c.id)}`}
                                                state={{ repoSearch: searchParams.toString() }}
                                                data-testid={`company-row-link-${c.id}`}
                                                className="text-slate-400 hover:text-slate-900"
                                                title="Open detail"
                                            >
                                                <ArrowRight size={16} />
                                            </Link>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100 text-xs text-slate-500">
                        <span data-testid="pagination-summary">
                            Page {page} of {pageCount} · {total.toLocaleString()} total
                        </span>
                        <div className="flex items-center gap-2">
                            <Button
                                variant="outline"
                                size="sm"
                                data-testid="page-prev"
                                disabled={page <= 1 || loading}
                                onClick={() => setPage((p) => Math.max(1, p - 1))}
                            >Prev</Button>
                            <Button
                                variant="outline"
                                size="sm"
                                data-testid="page-next"
                                disabled={page >= pageCount || loading}
                                onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
                            >Next</Button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
